import React, { useEffect, useRef, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Toaster } from '../components/ui/toaster';
import { 
  Database, Smartphone, Globe, Server, Code, Cloud, 
  ArrowRight, CheckCircle, Sparkles, Zap, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  database: Database,
  smartphone: Smartphone,
  globe: Globe,
  server: Server,
  code: Code,
  cloud: Cloud,
};

const ServicesPage = () => {
  const pageRef = useRef(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/services`)
      .then(res => res.json())
      .then(data => {
        setServices(data);
        setLoading(false);
      })
      .catch(() => {
        // Fallback to mock data if API fails
        setServices([
          { id: 'sap', title: 'SAP Services', icon: 'database', items: ['SAP S/4 HANA Migration', 'SAP Upgradation', 'SAP Implementation', 'SAP Consulting', 'SAP Support'] },
          { id: 'mobile', title: 'Mobile Development', icon: 'smartphone', items: ['Android Development', 'iOS Development', 'Cross-Platform Apps', 'Enterprise Mobile'] },
          { id: 'web', title: 'Web Development', icon: 'globe', items: ['Frontend Development', 'Backend Development', 'Full Stack', 'eCommerce'] },
          { id: 'database', title: 'Database Services', icon: 'server', items: ['Database Design', 'Migration', 'Administration', 'Optimization'] },
          { id: 'dotnet', title: '.NET Services', icon: 'code', items: ['.NET Core', 'ASP.NET', 'Entity Framework', 'Web APIs'] },
          { id: 'azure', title: 'Azure Services', icon: 'cloud', items: ['Azure AI', 'DevOps', 'App Services', 'Cloud Migration'] },
        ]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = pageRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [services]);

  const processSteps = [
    { step: '01', title: 'Discovery', description: 'Understanding your goals and challenges' },
    { step: '02', title: 'Design', description: 'Creating intuitive solutions' },
    { step: '03', title: 'Development', description: 'Building with best practices' },
    { step: '04', title: 'Deployment', description: 'Smooth launch and support' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30" ref={pageRef}>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="hero-orb hero-orb-1 opacity-40"></div>
            <div className="hero-orb hero-orb-2 opacity-30"></div>
            <div className="absolute inset-0 dot-grid opacity-20"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center reveal">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg shadow-orange-500/5 border border-orange-100/50 mb-6">
                <Zap className="w-4 h-4 text-[#ff6b35]" />
                <span className="text-sm font-medium text-gray-700">Comprehensive IT Solutions</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6" data-testid="services-hero-title">
                Our
                <span className="bg-gradient-to-r from-[#ff6b35] via-[#ff8f5c] to-[#ff6b35] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-x"> Services</span>
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed mb-10 max-w-2xl mx-auto">
                From SAP implementation to cloud migration, we deliver end-to-end solutions tailored to your business needs.
              </p>

              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="/contact"
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] text-white px-8 py-4 rounded-full font-semibold shadow-xl shadow-[#ff6b35]/30 hover:shadow-[#ff6b35]/50 hover:scale-[1.02] transition-all duration-300"
                  data-testid="services-cta-primary"
                >
                  Start a Project
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a 
                  href="#services-grid"
                  className="inline-flex items-center gap-2 px-6 py-4 text-gray-700 font-semibold hover:text-[#ff6b35] transition-colors"
                  data-testid="services-cta-secondary"
                >
                  Explore Services
                  <ChevronRight className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid - Bento Style */}
        <section id="services-grid" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <span className="inline-block px-4 py-2 bg-orange-50 text-[#ff6b35] rounded-full text-sm font-semibold mb-4">
                What We Offer
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Explore Our Expertise
              </h2>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="w-12 h-12 border-4 border-[#ff6b35] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
                {services.map((service, index) => {
                  const IconComponent = iconMap[service.icon] || Code;
                  return (
                    <div 
                      key={service.id}
                      className="reveal-scale group bento-card hover:border-[#ff6b35]/30 cursor-pointer"
                      style={{ animationDelay: `${index * 0.1}s` }}
                      data-testid={`service-card-${service.id}`}
                    >
                      <div className="flex items-start gap-4 mb-6">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <IconComponent className="w-7 h-7 text-[#ff6b35]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#ff6b35] transition-colors">
                            {service.title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">{service.items?.length || 0} services</p>
                        </div>
                      </div>
                      
                      <ul className="space-y-3">
                        {(service.items || []).slice(0, 5).map((item, i) => (
                          <li key={i} className="flex items-center gap-3 text-gray-600">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span className="text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-6 pt-6 border-t border-gray-100">
                        <a 
                          href={service.href || `/services/${service.id}`}
                          className="inline-flex items-center gap-2 text-[#ff6b35] font-semibold text-sm group-hover:gap-3 transition-all"
                        >
                          Learn More
                          <ArrowRight className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <span className="inline-block px-4 py-2 bg-white text-[#ff6b35] rounded-full text-sm font-semibold shadow-sm mb-4">
                Our Process
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                How We Work
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="process-grid">
              {processSteps.map((step, index) => (
                <div 
                  key={step.step}
                  className="reveal-scale relative"
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <div className="bento-card text-center relative z-10">
                    <div className="text-5xl font-bold text-[#ff6b35]/20 mb-4">{step.step}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                  
                  {/* Connector */}
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-[#ff6b35]/50 to-transparent z-0"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies Section */}
        <section className="py-20 bg-gray-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#ff6b35] rounded-full filter blur-[128px] opacity-20"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <span className="inline-block px-4 py-2 bg-white/10 text-[#ff6b35] rounded-full text-sm font-semibold mb-4">
                Technologies
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Tools We Work With
              </h2>
            </div>

            <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-4" data-testid="tech-grid">
              {['React', 'Node.js', 'Python', 'SAP', 'Azure', 'AWS', 'MongoDB', 'PostgreSQL', '.NET', 'Java', 'Docker', 'Kubernetes', 'Flutter', 'TypeScript', 'GraphQL', 'Redis'].map((tech, i) => (
                <div 
                  key={tech}
                  className="reveal-scale bg-white/5 backdrop-blur-sm rounded-xl p-4 text-center border border-white/10 hover:bg-white/10 hover:border-[#ff6b35]/50 transition-all duration-300 cursor-pointer"
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  <span className="text-white text-sm font-medium">{tech}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-[#ff6b35] to-[#ff8f5c] relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30"></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <Sparkles className="w-12 h-12 text-white/80 mx-auto mb-6 reveal" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 reveal" style={{ animationDelay: '0.1s' }}>
              Ready to Start Your Project?
            </h2>
            <p className="text-white/90 text-lg mb-10 reveal" style={{ animationDelay: '0.2s' }}>
              Let's discuss how our services can help transform your business.
            </p>
            <a 
              href="/contact"
              className="reveal inline-flex items-center gap-3 bg-white text-[#ff6b35] px-8 py-4 rounded-full font-semibold shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
              style={{ animationDelay: '0.3s' }}
              data-testid="services-cta-bottom"
            >
              Get Free Consultation
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default ServicesPage;
